import { forwardRef } from "react";
import {
  InformationCircleIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/outline";
import { truncateCourt } from "../../utils/truncateCourt";
import ColumnChooser from "./ColumnChooser";

const COURTS_INFO =
  'Τα δικαστήρια προς χάριν συντομίας εμφανίζονται, όπως εμφανίζονταν προ του Νέου Δικαστικού Χάρτη. Πχ. Το "Πρωτοδικείο Αθηνών (Πρώην Ειρηνοδικείο Αχαρνών)" εμφανίζεται ως "ΕΙΡ. ΑΧΑΡΝΩΝ". Παρακαλώ ελέγξτε ότι σας επιστρέφονται τα σωστά στοιχεία της υπόθεσής σας κατά την εισαγωγή.';

const FilterSelect = ({ label, value, onChange, options, allLabel = "Όλες" }) => (
  <label className={`ds-filter ${value ? "is-active" : ""}`}>
    <span className="ds-filter-label">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{allLabel}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </label>
);

const YES_NO = (yes, no) => [
  { value: "yes", label: yes },
  { value: "no", label: no },
];

const EPAN_OPTIONS = [
  { value: "pending", label: "Χρειάζονται αίτηση" },
  { value: "filed", label: "Κατατέθηκε αίτηση" },
  { value: "application", label: "Αιτήσεις επαναπροσδιορισμού" },
];

const CasesToolbar = forwardRef(
  (
    {
      search,
      onSearch,
      filters,
      onFilter,
      activeFilterCount,
      onClearFilters,
      courts,
      diadikasies,
      hasEpan,
      total,
      filteredTotal,
      onAddCase,
      onCheckAll,
      bulkChecking,
      columns,
      visibleColumnIds,
      onToggleColumn,
      onResetColumns,
      onShowHelp,
    },
    searchRef
  ) => (
    <div className="ds-toolbar">
      <div className="ds-toolbar-row">
        <label className="ds-search">
          <SearchIcon aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            placeholder="Αναζήτηση (ΓΑΚ, δικαστήριο, σημείωση)"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            aria-label="Αναζήτηση υποθέσεων"
          />
          <kbd className="ds-kbd" aria-hidden="true">
            /
          </kbd>
        </label>
        <span className="ds-count">
          {filteredTotal === total ? (
            <>
              <strong>{total}</strong> υποθέσεις
            </>
          ) : (
            <>
              <strong>{filteredTotal}</strong> από {total} υποθέσεις
            </>
          )}
        </span>
        <div className="ds-toolbar-spacer" />
        <button type="button" className="ds-btn ds-btn--primary" onClick={onAddCase}>
          <PlusIcon aria-hidden="true" />
          Νέα υπόθεση
        </button>
        <button
          type="button"
          className="ds-btn"
          onClick={onCheckAll}
          disabled={bulkChecking || total === 0}
          title="Μαζικός έλεγχος όλων των υποθέσεων (μία φορά την ημέρα)"
        >
          <RefreshIcon aria-hidden="true" className={bulkChecking ? "ds-spin" : ""} />
          {bulkChecking ? "Γίνεται εκκίνηση…" : "Έλεγχος όλων"}
        </button>
        <ColumnChooser
          columns={columns}
          visibleIds={visibleColumnIds}
          onToggle={onToggleColumn}
          onReset={onResetColumns}
        />
        <button
          type="button"
          className="ds-icon-btn"
          onClick={onShowHelp}
          aria-label="Συντομεύσεις πληκτρολογίου"
          title="Συντομεύσεις πληκτρολογίου ( ? )"
        >
          <QuestionMarkCircleIcon aria-hidden="true" />
        </button>
      </div>
      <div className="ds-toolbar-row ds-filters">
        <FilterSelect
          label="Απόφαση"
          value={filters.decisions}
          onChange={(v) => onFilter("decisions", v)}
          options={YES_NO("Με απόφαση", "Χωρίς απόφαση")}
        />
        <FilterSelect
          label="Συζήτηση"
          value={filters.syzitisi}
          onChange={(v) => onFilter("syzitisi", v)}
          options={YES_NO("Συζητηθείσες", "Μη συζητηθείσες")}
        />
        <FilterSelect
          label="Δικαστήριο"
          value={filters.court}
          onChange={(v) => onFilter("court", v)}
          options={courts.map((c) => ({ value: c, label: truncateCourt(c) }))}
          allLabel="Όλα"
        />
        <FilterSelect
          label="Διαδικασία"
          value={filters.diadikasia}
          onChange={(v) => onFilter("diadikasia", v)}
          options={diadikasies.map((d) => ({ value: d, label: d }))}
        />
        {(hasEpan || filters.epan) && (
          <FilterSelect
            label="Επαναπροσδιορισμός"
            value={filters.epan}
            onChange={(v) => onFilter("epan", v)}
            options={EPAN_OPTIONS}
          />
        )}
        {activeFilterCount > 0 && (
          <button type="button" className="ds-link-btn" onClick={onClearFilters}>
            Καθαρισμός φίλτρων ({activeFilterCount})
          </button>
        )}
        <span className="ds-info" tabIndex={0} title={COURTS_INFO} aria-label={COURTS_INFO}>
          <InformationCircleIcon aria-hidden="true" />
        </span>
      </div>
    </div>
  )
);

export default CasesToolbar;
